import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, CreditCard, Settings, LogOut, Zap, Bot, ShieldCheck, LifeBuoy, Upload, FolderOpen, KeyRound, ListOrdered, Sun, Moon, TriangleAlert, Images, Clapperboard, CalendarClock,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang, LangSelect } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { Modal } from "./common";

const NAV = [
  { to: "/app/dashboard", key: "dashboard", icon: LayoutDashboard, roles: ["super_admin", "reseller"], testid: "nav-dashboard-link" },
  { to: "/app/admin", key: "admin", icon: ShieldCheck, roles: ["super_admin"], testid: "nav-admin-link" },
  { to: "/app/bots", key: "bots", icon: Bot, roles: ["super_admin", "reseller"], testid: "nav-bots-link" },
  { to: "/app/uploads", key: "uploads", icon: Upload, roles: ["super_admin", "reseller"], testid: "nav-uploads-link" },
  { to: "/app/agendadas", key: "agendadas", icon: CalendarClock, roles: ["super_admin", "reseller"], testid: "nav-agendadas-link" },
  { to: "/app/fila", key: "fila", icon: ListOrdered, roles: ["super_admin", "reseller"], testid: "nav-fila-link" },
  { to: "/app/pastas", key: "pastas", icon: FolderOpen, roles: ["super_admin", "reseller"], testid: "nav-pastas-link" },
  { to: "/app/catalogo", key: "catalogo", icon: Clapperboard, roles: ["super_admin", "reseller"], testid: "nav-catalogo-link" },
  { to: "/app/capas", key: "capas", icon: Images, roles: ["super_admin", "reseller"], testid: "nav-capas-link" },
  { to: "/app/acessos", key: "acessos", icon: KeyRound, roles: ["super_admin", "reseller"], testid: "nav-acessos-link" },
  { to: "/app/billing", key: "billing", icon: CreditCard, roles: ["super_admin", "reseller"], testid: "nav-billing-link" },
  { to: "/app/suporte", key: "suporte", icon: LifeBuoy, roles: ["super_admin", "reseller"], testid: "nav-suporte-link" },
  { to: "/app/settings", key: "settings", icon: Settings, roles: ["super_admin", "reseller"], testid: "nav-settings-link" },
];

export default function AppShell() {
  const { user, setUser, logout } = useAuth();
  const { t, lang, langs } = useLang();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const items = NAV.filter((n) => n.roles.includes(user.role));
  const [showExpiry, setShowExpiry] = useState(false);

  // popup diário de renovação: aparece ao entrar quando faltam ≤7 dias (ou já venceu)
  const expDays = (() => {
    if (user.role === "super_admin" || !user.plan_id || !user.plan_expires_at) return null;
    const ms = new Date(user.plan_expires_at).getTime() - Date.now();
    return Math.ceil(ms / 86400000);
  })();
  const expiryKey = `cinebot_expiry_warn_${user.id}`;

  useEffect(() => {
    if (expDays === null || expDays > 7) return;
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem(expiryKey) !== today) setShowExpiry(true);
  }, [user.id, expDays]);

  const dismissExpiry = () => {
    localStorage.setItem(expiryKey, new Date().toISOString().slice(0, 10));
    setShowExpiry(false);
  };

  const locale = (langs.find((l) => l.code === lang) || langs[0]).locale;
  const dueDate = user.plan_expires_at ? new Date(user.plan_expires_at).toLocaleDateString(locale) : "";
  const countdownText = expDays === null ? "" : expDays < 0
    ? t("ew_expired").replace("%d", Math.abs(expDays))
    : expDays === 0 ? t("ew_today") : t("ew_days").replace("%d", expDays);

  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (data.balance_gb !== user.balance_gb) setUser({ ...user, ...data });
      } catch { /* silencioso */ }
    }, 20000);
    return () => clearInterval(t);
  }, [user.balance_gb]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const revertImpersonation = async () => {
    try {
      await api.post("/auth/impersonate-revert");
      window.location.assign("/app/admin");
    } catch {
      toast.error("Sessão de retorno expirada — faça login novamente");
      window.location.assign("/login");
    }
  };

  return (
    <div className="min-h-screen">
      <div className="bg-scene" />
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-[#080B11]/85 border-b border-[#232D42] px-4 sm:px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5" data-testid="app-logo">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
            <Zap size={16} className="text-slate-950" strokeWidth={2.5} />
          </div>
          <span className="font-display font-bold text-white tracking-tight">CINE<span className="neon-text">BOT</span></span>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="theme-toggle" onClick={toggle} title={theme === "dark" ? t("theme_light") : t("theme_dark")} className="p-2 rounded-md border border-[#232D42] text-slate-400 hover:text-amber-400 hover:border-amber-500/40 transition-colors">
            {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
          </button>
          <LangSelect testid="lang-select-header" />
          {user.avatar_path && (
            <img
              data-testid="user-avatar"
              src={`${process.env.REACT_APP_BACKEND_URL}/api/auth/avatar/${user.id}`}
              alt="Foto de perfil"
              className="w-8 h-8 rounded-xl object-cover border border-slate-700"
            />
          )}
          <div className="hidden sm:block text-right">
            <p className="text-xs font-semibold text-white leading-tight" data-testid="user-name">{user.name}</p>
            <p className="text-[10px] font-mono2 uppercase tracking-widest text-amber-500/90" data-testid="user-role">{t(user.role === "super_admin" ? "role_admin" : "role_reseller")}</p>
          </div>
          <button data-testid="logout-btn" onClick={handleLogout} title={t("logout")} className="p-2 rounded-xl btn-ghost-dark text-slate-400 hover:text-white">
            <LogOut size={16} />
          </button>
        </div>
      </header>

      {user.impersonating && (
        <div data-testid="impersonation-banner" className="fixed top-[73px] left-0 right-0 z-[60] bg-amber-400 text-slate-950 px-4 py-2 flex items-center justify-center gap-3 text-xs font-semibold">
          <TriangleAlert size={14} className="shrink-0" />
          <span className="truncate">A ver como <b>{user.name}</b> ({user.email}){user.is_test ? " · CONTA DE TESTE" : ""}{user.impersonator?.name ? ` — admin: ${user.impersonator.name}` : ""}</span>
          <button data-testid="impersonation-revert-btn" onClick={revertImpersonation} className="px-3 py-1 rounded-lg bg-slate-950 text-amber-300 text-[11px] font-bold hover:bg-slate-800 transition-colors shrink-0">Voltar ao admin</button>
        </div>
      )}

      <aside className="fixed left-0 top-[73px] bottom-0 w-64 backdrop-blur-md bg-[#080B11]/75 border-r border-[#232D42] p-4 space-y-1.5 z-40 hidden md:block">
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={n.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3.5 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive
                  ? "bg-[#161D2E] text-amber-400 font-semibold border border-[#232D42] border-l-2 border-l-amber-500 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]"
                  : "text-slate-400 hover:text-slate-100 hover:bg-[#161D2E] border border-transparent"
              }`
            }
          >
            <n.icon size={17} />
            {t(n.key)}
          </NavLink>
        ))}
      </aside>

      <main className={`${user.impersonating ? "pt-36" : "pt-24"} pb-28 md:pb-12 px-4 sm:px-6 md:pl-72 md:pr-8 max-w-[1400px] mx-auto space-y-6`}>
        <Outlet />
      </main>

      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 backdrop-blur-md bg-[#080B11]/92 border-t border-[#232D42] px-2 py-2 flex justify-around">
        {items.filter((n) => n.to !== "/app/telegram").slice(0, 7).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={`${n.testid}-mobile`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 px-2 py-1.5 rounded-lg text-[10px] font-medium transition-colors ${
                isActive ? "text-amber-400" : "text-slate-500"
              }`
            }
          >
            <n.icon size={18} />
            {t(n.key).split(" ")[0]}
          </NavLink>
        ))}
      </nav>

      {expDays !== null && expDays <= 7 && (
        <Modal open={showExpiry} onClose={dismissExpiry} title={expDays < 0 ? t("ew_title_expired") : t("ew_title")} testid="expiry-warning-modal">
          <div className="space-y-4">
            <div className={`rounded-xl border px-4 py-4 flex items-start gap-3 ${expDays <= 1 ? "border-rose-400/40 bg-rose-500/10" : "border-amber-400/30 bg-amber-400/10"}`} data-testid="expiry-alert-box">
              <TriangleAlert size={22} className={`${expDays <= 1 ? "text-rose-400" : "text-amber-300"} shrink-0 mt-0.5`} />
              <div>
                <p className={`text-base font-bold ${expDays <= 1 ? "text-rose-300" : "text-amber-200"}`} data-testid="expiry-countdown">{countdownText}</p>
                <p className="text-xs text-slate-300 mt-1" data-testid="expiry-due-date">
                  {t("ew_due")}: <span className="font-mono2 text-white">{dueDate}</span>{user.plan_name ? ` · ${user.plan_name}` : ""}
                </p>
              </div>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed" data-testid="expiry-warning-text">{t("ew_warn")}</p>
            <div className="flex flex-wrap gap-3 justify-end">
              <button data-testid="expiry-remind-later" onClick={dismissExpiry} className="btn-ghost-dark px-5 py-2.5 rounded-xl text-xs text-slate-300">{t("ew_later")}</button>
              <button
                data-testid="expiry-renew-now"
                onClick={() => { dismissExpiry(); navigate("/app/billing"); }}
                className="btn-neon px-5 py-2.5 rounded-xl text-xs font-semibold inline-flex items-center gap-2"
              >
                <CreditCard size={14} /> {t("ew_cta")}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
